/**
 * OaSIS Explorer Frontend - Translation System
 * Task 3.1: Frontend Foundation & Global Features
 * 
 * Bilingual content management for EN/FR support
 */

import type { Language } from '../types';

// Translation dictionary
const translations = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.occupations': 'Search',
    'nav.taxonomy': 'Occupations',
    'nav.measurements': 'Skills & Competencies',
    'nav.classification': 'Occupations',
    'nav.sct': 'Skills',
    'nav.about': 'About',
    'nav.datasets': 'Datasets',
    'nav.user': 'User',
    'nav.savedSearches': 'Saved Searches',
    'version.provenance.snapshotCaptured': 'Source snapshot captured',
    'version.provenance.packageModified': 'Gov package modified',
    'version.provenance.notAvailable': 'Not available',
    'discrepancy.search.title': 'Known data discrepancy',
    'discrepancy.search.message': 'One or more selected filters are known to differ between the official OaSIS Advanced Search interface and Open Data. Results shown here reflect Open Data values.',
    'discrepancy.search.affectedFilters': 'Affected selected filters',
    'discrepancy.occupation.title': 'Known data discrepancy',
    'discrepancy.occupation.message': 'This occupation appears in known mismatch rows.',
    'discrepancy.occupation.messagePrefix': 'This occupation appears in known mismatch rows between',
    'discrepancy.occupation.messageAnd': 'and',
    'discrepancy.occupation.affectedFields': '{count} affected fields',
    'discrepancy.occupation.showDetails': 'Show discrepancy details ({count})',
    'discrepancy.occupation.showLess': 'Show less',
    'discrepancy.occupation.showingRows': 'Showing {shown} of {total} discrepancy rows',
    'discrepancy.common.officialValue': 'Official value',
    'discrepancy.common.openDataValue': 'Open Data value',
    
    // Language switching
    'language.switch': 'Switch to French',
    'language.current': 'English',
    
    // Theme switching
    'theme.switch': 'Switch to dark mode',
    'theme.light': 'Light mode',
    'theme.dark': 'Dark mode',
    
    // Common UI
    'common.loading': 'Loading...',
    'common.error': 'An error occurred',
    'common.retry': 'Retry',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.search': 'Search',
    'common.clear': 'Clear',
    'common.close': 'Close',
    'common.cancel': 'Cancel',
    'common.email': 'Email',
    
    // App title and description
    'app.title': 'OaSIS Explorer',
    'app.subtitle': 'Alternative Interface for the Government of Canada\'s Occupational and Skills Information System',
    'app.description': 'Explore occupations using official ESDC data from the Government of Canada',
    'app.welcome': 'Welcome to the OaSIS Explorer',
    'app.official.title': 'Official Government Data',
    'app.official.description': 'This is an alternative interface for the Occupational and Skills Information System (OaSIS) from Employment and Social Development Canada. All data is sourced directly from the official government datasets without modification.',
    'app.data.source': 'Data Source',
    'app.data.dataset': 'Dataset',
    'app.data.program': 'Available through Canada\'s Open Government Program',
    'app.links.official': 'Official OaSIS System',
    'app.links.dataset': 'Open Canada Data Portal',
    'app.links.government': 'Open Government Canada',
    
    // Feature badges
    'badges.core': 'Core Feature',
    'badges.navigation': 'Navigation',
    
    // Feature cards
    'features.search.title': 'Advanced Search',
    'features.search.description': 'Find and explore detailed information about Canadian occupations with advanced search capabilities.',
    'features.search.button': 'Start Searching',
    'features.taxonomy.title': 'Navigate Occupations Taxonomy',
    'features.taxonomy.description': 'Navigate through the structured classification system of occupation classifications and categories.',
    'features.taxonomy.button': 'Explore Taxonomy',
    'features.measurements.title': 'Navigate Skills & Competencies Taxonomy',
    'features.measurements.description': 'Explore the Skills and Competencies Taxonomy (SCT) hierarchy with measurement descriptors and analysis tools.',
    'features.measurements.button': 'Explore Taxonomy',
    'features.details.title': 'Occupation Details',
    'features.details.description': 'Access comprehensive occupational profiles with skills and requirements.',
    'features.details.button': 'Browse Occupational Profiles',
    'features.about.title': 'About & Documentation',
    'features.about.description': 'Learn about the Occupational and Skills Information System project, data sources, and technical documentation.',
    'features.about.button': 'Learn More',
    
    // Homepage content
    'home.hero.explore': 'Explore Canadian Occupation Data',
    'home.hero.subtitle': 'Access comprehensive occupation analysis tools with advanced search, classification system navigation, and detailed measurements.',
    'home.benefits.title': 'Why Choose the Occupational and Skills Information System?',
    'home.benefits.subtitle': 'Built for researchers, career counselors, and policy makers with cutting-edge technology and user-centered design.',
    'home.benefits.fast.title': 'Lightning Fast',
    'home.benefits.fast.description': 'Sub-200ms interactions, optimized builds, and responsive design for excellent user experience.',
    'home.benefits.saved.title': 'Saved Searches',
    'home.benefits.saved.description': 'Save your searches for later and quickly return to your research without starting over.',
    'home.benefits.navigate.title': 'Intuitive Navigation',
    'home.benefits.navigate.description': 'Streamlined interface designed for researchers with clear navigation paths and logical information organization.',
    'home.stats.occupations': 'Occupations',
    'home.stats.measurements': 'Measurements',
    
    // Advanced Search page
    'search.title': 'Advanced Search',
    'search.subtitle': 'Find occupations using comprehensive search and filtering capabilities',
    'search.autocomplete.placeholder': 'Start typing to search occupations...',
    'search.results.count': 'occupations match your criteria',
    'search.results.showing': 'Showing',
    'search.results.of': 'of',
    'search.filters.title': 'Filters',
    'search.filters.clear': 'Clear All Filters',
    'search.filters.apply': 'Apply Filters',
    'search.filters.textFilter': 'Text Filter',
    'search.filters.textFilter.placeholder': 'Enter occupation title or keywords...',
    'search.filters.hierarchyCode': 'Broad Category Filter',
    'search.filters.hierarchyCode.description': 'Select occupation categories to filter by classification code',
    'search.filters.teerLevel': 'TEER Level Filter',
    'search.filters.teerLevel.description': 'Select TEER levels to filter by training, education, experience, and responsibilities',
    'search.filters.teer.0': 'TEER 0 - Management occupations',
    'search.filters.teer.1': 'TEER 1 - Occupations usually require a university degree',
    'search.filters.teer.2': 'TEER 2 - Occupations usually require a college diploma or apprenticeship training of two or more years; or supervisory occupations',
    'search.filters.teer.3': 'TEER 3 - Occupations usually require a college diploma or apprenticeship training of less than two years; or more than six months of on-the-job training',
    'search.filters.teer.4': 'TEER 4 - Occupations usually require a secondary school diploma; or several weeks of on-the-job training',
    'search.filters.teer.5': 'TEER 5 - Occupations usually require short-term work demonstration and no formal education',
    'search.filters.measurements': 'Measurement Filters',
    'search.filters.measurements.description': 'Filter by skills, abilities, work context, and other measurements',
    'search.filters.hierarchy': 'Classification Structure Filter',
    'search.filters.text': 'Text Search',
    'search.filters.category': 'Category',
    'search.filters.teer': 'TEER',
    'search.filters.range.min': 'Minimum',
    'search.filters.range.max': 'Maximum',
    'search.filters.range.selected': 'Selected range',
    'search.filters.range.none': 'No range selected',
    'search.filters.track.mobile.instructions': 'Tap to set minimum value',
    'search.filters.track.mobile.tapMax': 'Tap to set maximum value',
    'search.filters.track.mobile.tapMin': 'Tap to set new minimum value',
    'search.loading': 'Searching...',
    'search.error': 'Search failed. Please try again.',
    'search.no-results': 'No occupations found matching your criteria.',
    'search.suggestions': 'Search suggestions',
    'search.min-chars': 'Type at least 2 characters to search',
    'search.saveButton': 'Save this search',
    // Task 30.7: Manual search button
    'search.searchButton': 'Search Occupations',
    'search.searching': 'Searching...',
    'search.readyToSearch': 'Ready to search',
    'search.clickToExecute': 'Click the button to find matching occupations.',
    'search.loadingCount': 'Counting matching occupations...',
    'search.countPreview': 'occupations match your filters',
    'search.saveDialog.title': 'Save Search',
    'search.saveDialog.nameLabel': 'Search Name',
    'search.saveDialog.namePlaceholder': 'Enter a name for this search...',
    'search.saveDialog.characters': 'characters',
    'search.saveDialog.errorEmpty': 'Please enter a name for this search',
    'search.saveDialog.errorTooLong': 'Search name must be 50 characters or less',
    'search.saveDialog.errorDuplicate': 'A search with this name already exists',
    'search.saveDialog.saveButton': 'Save',
    'search.saveSuccess': 'Search saved as "{name}"',
    'search.refineYourSearch': 'Refine Your Search',
    'search.adjustFiltersToRefine': 'Adjust filters below to refine your results',
    
    // Search Results page
    'searchResults.title': 'Search Results',
    'searchResults.backToFilters': 'Back to Filters',
    'searchResults.viewFullResults': 'View Results',
    'searchResults.noResultsFound': 'No results found',
    'searchResults.tryAdjustingFilters': 'Try adjusting your filters or search terms',
    'searchResults.loadingResults': 'Loading results...',
    'searchResults.error': 'Error Loading Results',
    'searchResults.sortBy': 'Sort by',
    'searchResults.sortTitleAsc': 'Title (A-Z)',
    'searchResults.sortTitleDesc': 'Title (Z-A)',
    'searchResults.sortCodeAsc': 'Code (Ascending)',
    'searchResults.sortCodeDesc': 'Code (Descending)',
    
    // User page
    'user.title': 'My Saved Searches',
    'user.subtitle': 'Access and manage your saved search configurations',
    'user.savedSearches': 'saved searches',
    'user.loading': 'Loading saved searches...',
    'user.empty.title': 'No saved searches yet',
    'user.empty.description': 'Save your search configurations on the Advanced Search page to access them here later.',
    'user.empty.goToSearch': 'Go to Advanced Search',
    'user.bookmarked.title': 'Bookmarked',
    'user.allSearches.title': 'All Saved Searches',
    'user.filterByTags': 'Filter by Tags',
    'user.filterButton': 'Filter',
    'user.noFilteredResults': 'No saved searches match the selected tags.',
    'user.deleteConfirm.title': 'Delete Saved Search',
    'user.deleteConfirm.message': 'Are you sure you want to delete "{name}"? This action cannot be undone.',
    'user.deleteConfirm.deleteButton': 'Delete',
    
    // Taxonomy page
    'taxonomy.title': 'Occupations',
    'taxonomy.subtitle': 'Navigate the occupation classification system structure',
    
    // Classification page
    'classification.treeView': 'Tree',
    'classification.listView': 'Table',
    'classification.treeTitle': 'Occupations Tree',
    'classification.listTitle': 'Occupations Table',
    'classification.occupations': 'occupations',
    'classification.oasisCode': 'OaSIS Code',
    'classification.occupationTitle': 'Occupation Title',
    'classification.noResults': 'No occupations match your search',
    'classification.noOccupations': 'No occupations found',
    'classification.loading': 'Loading occupations...',
    'classification.error': 'Failed to load occupations. Please try again.',
    'classification.expandHint': 'Expand items to view hierarchy',
    'classification.broadOccCat': 'Broad Occ. Cat.',
    'classification.teer': 'TEER',
    'classification.majorGroup': 'Major Group',
    'classification.subMajorGroup': 'Sub-major Group',
    'classification.minorGroup': 'Minor Group',
    'classification.nocUnitGroup': 'NOC Unit Group',
    
    // Measurements page
    'measurements.title': 'Skills & Competencies',
    'measurements.subtitle': 'Navigate the skills and competencies (SCT) structure',
    'measurements.search.placeholder': 'Search SCT code or descriptor title...',
    'classification.search.placeholder': 'Search OaSIS code or occupation title...',
    'measurements.coming.title': 'Skills and Competencies Taxonomy (SCT) Navigation Coming Soon',
    'measurements.coming.description': 'Interactive Skills and Competencies Taxonomy (SCT) navigation with search, filtering, and measurement exploration will be available in the next phase.',
    'measurements.structure.title': 'Skills and Competencies Taxonomy (SCT) Structure',
    'sct.treeTitle': 'Skills and Competencies Taxonomy Tree',
    'sct.listTitle': 'Skills and Competencies Taxonomy Table',
    
    // SCT Table View
    'sct.table.treeView': 'Tree',
    'sct.table.listView': 'Table',
    'sct.table.entries': 'entries',
    'sct.table.code': 'SCT Code',
    'sct.table.title': 'Title',
    'sct.table.hierarchyLevel': 'Hierarchy Level',
    'sct.table.category': 'Category',
    'sct.table.subCategory': 'Sub-Category',
    'sct.table.similarityGroup': 'Similarity Group',
    'sct.table.descriptor': 'Descriptor',
    'sct.table.noResults': 'No entries match your search',
    'sct.table.noEntries': 'No entries found',
    'sct.table.loading': 'Loading entries...',
    'sct.table.error': 'Failed to load entries. Please try again.',
    'sct.table.expandHint': 'Expand items to view hierarchy',

    // Occupation details page
    'occupation.details.title': 'Occupation Details',
    'occupation.details.loading': 'Loading occupation details...',
    'occupation.details.notFound': 'Occupation Not Found',
    'occupation.details.error': 'Error Loading Occupation',
    'occupation.details.back': 'Back',
    'occupation.details.export': 'Export Profile',
    'occupation.details.overview': 'Occupation Overview',
    'occupation.details.statistics': 'Data Statistics',
    'occupation.details.completeness': 'Data Completeness',
    'occupation.details.totalMeasurements': 'Total Measurements',
    'occupation.details.withValues': 'With Values',
    'occupation.details.measurements': 'Skills and Competencies Taxonomy (SCT) Measurements',
    'occupation.details.interests': 'Interest Profile (Holland Codes)',
    'occupation.details.additional': 'Additional Information',
    'occupation.details.requirements': 'Employment Requirements',
    'occupation.details.duties': 'Main Duties',
    'occupation.details.titles': 'Example Job Titles',
    'occupation.details.titles.more': 'more',
    'occupation.details.titles.showMore': 'Show all job titles',
    'occupation.details.titles.showLess': 'Show less',
    'occupation.details.workplace': 'Workplace Information',
    'occupation.findSimilar': 'Find Similar Occupations',
    'occupation.similarOccupations': 'Similar Occupations',
    'occupation.similarOccupationsDescription': 'Search for occupations with similar skills, abilities, knowledge, and work context.',
    'occupation.prefillModal.title': 'Find Similar Occupations',
    'occupation.prefillModal.description': 'Select which characteristics to use for finding similar occupations',
    'occupation.prefillModal.searchMode': 'Include occupations with lower values',
    'occupation.prefillModal.searchModeExact': 'Exact match: Find occupations with the same values',
    'occupation.prefillModal.searchModeLTE': 'Range match: Find occupations with values from 0 to this occupation\'s values',
    'occupation.prefillModal.categories': 'Categories',
    'occupation.prefillModal.selectAll': 'All',
    'occupation.prefillModal.deselectAll': 'None',
    'occupation.prefillModal.categoriesSelected': '{{count}} categories selected',
    'occupation.prefillModal.noCategoriesSelected': 'Please select at least one category',
    'occupation.prefillModal.noCategories': 'No measurement categories available',
    'occupation.prefillModal.countLoading': 'Counting results...',
    'occupation.prefillModal.countError': 'Unable to count results',
    'occupation.prefillModal.resultCount': '{{count}} matching occupations ({{time}}ms)',
    'occupation.prefillModal.search': 'Search',
    'occupation.prefillModal.cancel': 'Cancel',
    
    // Core Competencies (Task 33.4 - 2025 only)
    'occupation.coreCompetencies.title': 'Core Competencies',
    'occupation.coreCompetencies.description': 'Key competencies required for this occupation',
    'occupation.coreCompetencies.loading': 'Loading competencies...',
    
    // Skills Match (Task 33.6B - 2025 only)
    'occupation.skillsMatch.description': 'Occupations with similar skills, abilities, and knowledge profiles based on OaSIS analysis.',
    'occupation.skillsMatch.unavailable': 'Direct skills match recommendations are not available for this dataset version. Use the Find Similar button below to search manually.',
    'occupation.skillsMatch.empty': 'No skills match data available for this occupation.',
    'occupation.skillsMatch.error': 'Could not load skills match recommendations. Try refreshing the page.',
    'occupation.skillsMatch.verySimilar': 'Very Similar',
    'occupation.skillsMatch.similar': 'Similar',
    'occupation.skillsMatch.somewhatSimilar': 'Somewhat Similar',
    'occupation.skillsMatch.notSimilar': 'Not Similar',
    
    // Footer links
    'footer.github': 'GitHub',
    'footer.documentation': 'Documentation',
    'footer.api': 'API Reference',
    
    // Error messages
    'error.network': 'Network connection error',
    'error.api': 'API request failed',
    'error.notFound': 'Resource not found',
    'error.serverError': 'Server error occurred',
    'error.tryAgain': 'Please try again later',
    
    // Accessibility
    'a11y.skipToContent': 'Skip to main content',
    'a11y.mainMenu': 'Main navigation menu',
    'a11y.languageToggle': 'Change language',
    'a11y.themeToggle': 'Toggle dark mode',
    
    // Authentication
    'auth.signIn': 'Sign In',
    'auth.signUp': 'Sign Up',
    'auth.signOut': 'Sign Out',
    'auth.signingIn': 'Signing in...',
    'auth.creatingAccount': 'Creating account...',
    'auth.createAccount': 'Create Account',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.confirmPassword': 'Confirm Password',
    'auth.displayName': 'Display Name',
    'auth.forgotPassword': 'Forgot password?',
    'auth.noAccount': "Don't have an account?",
    'auth.haveAccount': 'Already have an account?',
    'auth.resetPassword': 'Reset Password',
    'auth.resetPasswordDescription': "Enter your email and we'll send you a link to reset your password.",
    'auth.sendResetLink': 'Send Reset Link',
    'auth.sending': 'Sending...',
    'auth.resetEmailSent': 'Check Your Email',
    'auth.resetEmailSentDescription': "We've sent a password reset link to your email address.",
    'auth.backToSignIn': 'Back to Sign In',
    'auth.passwordMismatch': 'Passwords do not match.',
    'auth.passwordTooShort': 'Password must be at least 6 characters.',

    // Auth Prompt Modal (Task 30.5)
    'auth.prompt.title': 'Sign in to access {feature}',
    'auth.prompt.description': 'Create a free account to unlock this feature.',
    'auth.prompt.benefitsTitle': 'With a free account, you can:',
    'auth.prompt.benefit.savedSearches': 'Save up to 3 searches',
    'auth.prompt.benefit.moreSearches': '{freeLimit} searches per day (vs {anonymousLimit} anonymous)',
    'auth.prompt.benefit.crossDevice': 'Access from any device',
    'auth.prompt.signIn': 'Sign In',
    'auth.prompt.createAccount': 'Create Free Account',
    'auth.prompt.maybeLater': 'Maybe Later',
    
    // Common additions
    'common.optional': 'optional',
    'common.edit': 'Edit',
    'common.save': 'Save',
    
    // Profile page
    'profile.title': 'Profile & Settings',
    'profile.subtitle': 'Manage your account and preferences',
    'profile.profileSection': 'Profile',
    'profile.settingsSection': 'Settings',
    'profile.accountSection': 'Account',
    'profile.displayName': 'Display Name',
    'profile.noName': 'No name set',
    'profile.email': 'Email',
    'profile.emailReadOnly': 'Email cannot be changed',
    'profile.memberSince': 'Member Since',
    'profile.language': 'Language',
    'profile.theme': 'Theme',
    'profile.themeLight': 'Light',
    'profile.themeDark': 'Dark',
    'profile.changePassword': 'Change Password',
    'profile.passwordResetSent': 'Reset email sent! Check your inbox.',
    'profile.passwordResetError': 'Failed to send reset email.',
    'nav.profile': 'Profile',
    
    // Subscription
    'profile.subscriptionSection': 'Subscription',
    'profile.currentPlan': 'Current Plan',
    'profile.freePlan': 'Free',
    'profile.proPlan': 'Pro',
    'profile.upgradeToProTitle': 'Upgrade to Pro',
    'profile.upgradeToProDescription': 'Unlock all features including unlimited saved searches and exports.',
    'profile.monthly': 'Monthly',
    'profile.annual': 'Annual',
    'profile.monthlyPrice': '$50/month',
    'profile.annualPrice': '$480/year',
    'profile.annualSavings': 'Save 20%',
    'profile.upgradeButton': 'Upgrade',
    'profile.manageSubscription': 'Manage Subscription',
    'profile.nextBilling': 'Next billing date',
    'profile.cancelingOn': 'Cancels on',
    'profile.pastDue': 'Payment past due - please update your payment method.',
    'profile.updatePayment': 'Update Payment Method',
    'profile.checkoutSuccess': 'Welcome to Pro! Your subscription is now active.',
    'profile.checkoutCanceled': 'Checkout was canceled. No charges were made.',
    'profile.subscriptionError': 'Failed to fetch subscription status. Please try again later.',
    'profile.profileError': 'Failed to fetch profile information. Please try again later.',

    // Search Usage Banner (Task 30.9)
    'usage.banner.freePlan': 'Free Plan',
    'usage.banner.guest': 'Guest',
    'usage.banner.searchesLeft': '{count} searches left today',
    'usage.banner.oneSearchLeft': '1 search left today',
    'usage.banner.noSearchesLeft': 'No searches left today',
    'usage.banner.runningLow': 'Running low!',
    'usage.banner.resetsIn': 'Resets in {hours}h',
    'usage.banner.getUnlimited': 'Get Unlimited',
    'usage.banner.signUpFree': 'Sign Up Free',

    // Usage Limits (Task 30.2)
    'usage.remaining': '{remaining} of {limit} searches remaining today',
    'usage.remainingOne': '1 search remaining today',
    'usage.remainingNone': 'No searches remaining today',
    'usage.unlimited': 'Unlimited searches',
    'usage.resetsIn': 'Resets in {hours} hours',
    'usage.resetsAt': 'Resets at {time}',

    // Limit Reached Modal (Task 30.2)
    'limit.title': 'Daily Search Limit Reached',
    'limit.messageAnonymous': 'Anonymous users can make {anonymousLimit} searches per day.',
    'limit.messageFree': 'Free accounts can make {freeLimit} searches per day.',
    'limit.resetTime': 'Your limit resets in {hours} hours.',
    'limit.signUpFree': 'Sign Up Free',
    'limit.signUpBenefit': 'Get {freeLimit} searches per day with a free account!',
    'limit.upgradeToPro': 'Upgrade to Pro',
    'limit.upgradeBenefit': 'Unlimited searches and premium features.',
    'limit.maybeLater': 'Maybe Later',
    'limit.waitForReset': 'Wait for Reset',

    // Upgrade CTA (Task 30.2)
    'upgrade.title': 'Upgrade to Pro',
    'upgrade.description': 'Get unlimited searches and premium features.',
    'upgrade.cta': 'Upgrade Now',
    'upgrade.benefits.unlimited': 'Unlimited searches',
    'upgrade.benefits.filters': 'All filter types including Interests',
    'upgrade.benefits.exports': 'Export data to CSV/PDF',
    'upgrade.benefits.savedSearches': 'Unlimited saved searches',

    // Feature Gating (Task 30.6)
    'feature.proBadge': 'Pro',
    'feature.locked': 'Locked',
    'feature.proOnly': 'Pro Only',
    'feature.accountRequired': 'Account Required',
    'feature.upgradeToUse': 'Upgrade to Pro to use {feature}',
    'feature.signUpToUse': 'Sign up free to use {feature}',
    'feature.tooltip': 'This feature is only available for Pro users',
    'feature.accessDenied.title': 'Pro Feature',
    'feature.accessDenied.message': '{feature} is only available for Pro users.',
    'feature.accessDenied.upgrade': 'Upgrade to Pro',
    'feature.accessDenied.maybeLater': 'Maybe Later',
    'feature.interestsFilter': 'Interests Filter',
    'feature.interestsFilterDescription': 'Filter occupations by Holland Code interests (RIASEC)',

    // Privacy Policy (Task 29.1A)
    'privacy.title': 'Privacy Policy',
    'privacy.lastUpdated': 'Last Updated',
    'privacy.intro.title': 'Introduction',
    'privacy.intro.description': 'OaSIS Explorer is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our service.',
    'privacy.collection.title': 'Information We Collect',
    'privacy.collection.description': 'We collect the following types of information:',
    'privacy.collection.email.title': 'Email Address',
    'privacy.collection.email.description': 'Used for authentication and account management',
    'privacy.collection.profile.title': 'Profile Information',
    'privacy.collection.profile.description': 'Optional display name and preferences you provide',
    'privacy.collection.usage.title': 'Usage Data',
    'privacy.collection.usage.description': 'Search queries, saved searches, bookmarked occupations, and feature usage',
    'privacy.collection.technical.title': 'Technical Data',
    'privacy.collection.technical.description': 'IP address, browser type, device information, and session data',
    'privacy.usage.title': 'How We Use Your Information',
    'privacy.usage.auth': 'To provide authentication and account access',
    'privacy.usage.service': 'To deliver and improve our services',
    'privacy.usage.communication': 'To send transactional emails (password resets, subscription confirmations)',
    'privacy.usage.analytics': 'To analyze usage patterns and improve user experience',
    'privacy.usage.improvement': 'To develop new features and maintain service quality',
    'privacy.storage.title': 'Data Storage and Processing',
    'privacy.storage.description': 'Your data is stored securely using industry-standard practices:',
    'privacy.storage.firebase': 'Handles authentication and email verification',
    'privacy.storage.gcp': 'Hosts our application and database infrastructure',
    'privacy.storage.database': 'Stores user profiles, subscriptions, and saved data',
    'privacy.thirdParty.title': 'Third-Party Services',
    'privacy.thirdParty.firebase': 'Authentication services',
    'privacy.thirdParty.resend': 'Transactional email delivery (password resets, notifications)',
    'privacy.thirdParty.stripe': 'Payment processing for Pro subscriptions (we do not store payment details)',
    'privacy.thirdParty.policies': 'These services have their own privacy policies and security practices.',
    'privacy.cookies.title': 'Cookies and Tracking',
    'privacy.cookies.description': 'We use cookies and similar technologies for:',
    'privacy.cookies.auth.title': 'Authentication',
    'privacy.cookies.auth.description': 'To keep you signed in securely',
    'privacy.cookies.session.title': 'Session Management',
    'privacy.cookies.session.description': 'To track anonymous usage for rate limiting',
    'privacy.cookies.preferences.title': 'Preferences',
    'privacy.cookies.preferences.description': 'To remember your language and theme choices',
    'privacy.rights.title': 'Your Rights',
    'privacy.rights.access.title': 'Access',
    'privacy.rights.access.description': 'View your personal data through your profile page',
    'privacy.rights.correct.title': 'Correction',
    'privacy.rights.correct.description': 'Update your profile information at any time',
    'privacy.rights.delete.title': 'Deletion',
    'privacy.rights.delete.description': 'Request account deletion by contacting us',
    'privacy.rights.export.title': 'Export',
    'privacy.rights.export.description': 'Request a copy of your data',
    'privacy.security.title': 'Data Security',
    'privacy.security.description': 'We implement appropriate technical and organizational security measures to protect your personal information, including encryption, secure authentication, and regular security audits.',
    'privacy.children.title': 'Children\'s Privacy',
    'privacy.children.description': 'OaSIS Explorer is not intended for children under 13. We do not knowingly collect personal information from children.',
    'privacy.changes.title': 'Changes to This Policy',
    'privacy.changes.description': 'We may update this Privacy Policy from time to time. We will notify users of any material changes by email or through a notice on our website.',
    'privacy.contact.title': 'Contact Us',
    'privacy.contact.description': 'If you have questions about this Privacy Policy or your data, please contact us:',

    // Navigation
    'nav.privacy': 'Privacy',
    'nav.terms': 'Terms',
    'nav.contact': 'Contact',

    // Footer (Task 29.1C)
    'footer.about.description': 'A comprehensive tool for exploring ESDC data',
    'footer.links.title': 'Quick Links',
    'footer.contact.title': 'Contact Us',
    'footer.contact.supportHours': 'We typically respond within 24-48 hours',
    'footer.rights': 'All rights reserved.',
    'footer.dataSource': 'Data provided by Employment and Social Development Canada (ESDC)',

    // Contact Page (Task 29.1C)
    'contact.title': 'Contact Us',
    'contact.description': 'If you have questions, feedback, or need assistance with OaSIS Explorer, please reach out.',
    'contact.methods.title': 'How to Reach Us',
    'contact.methods.email.title': 'Email Support',
    'contact.methods.email.description': 'For general inquiries, account issues, or technical support:',
    'contact.methods.email.responseTime': 'Typical response time: 24-48 hours',
    'contact.hours.title': 'Support Hours',
    'contact.hours.description': 'Monday - Friday, 9:00 AM - 5:00 PM EST (excluding holidays)',
    'contact.topics.title': 'Common Inquiry Topics',
    'contact.topics.account.title': 'Account & Authentication',
    'contact.topics.account.description': 'Password resets, account deletion, sign-in issues',
    'contact.topics.billing.title': 'Billing & Subscriptions',
    'contact.topics.billing.description': 'Payment questions, plan changes, invoices',
    'contact.topics.technical.title': 'Technical Issues',
    'contact.topics.technical.description': 'Bugs, errors, feature requests',
    'contact.topics.data.title': 'Data Questions',
    'contact.topics.data.description': 'Occupational data accuracy or interpretation (see note below)',
    'contact.topics.feedback.title': 'Feedback',
    'contact.topics.feedback.description': 'Suggestions, feature requests, general comments',
    'contact.dataInquiries.title': 'Data-Related Inquiries',
    'contact.dataInquiries.description': 'For questions about the accuracy or interpretation of occupational data, please contact Employment and Social Development Canada (ESDC) directly, as they maintain the OaSIS database:',
    'contact.dataInquiries.link': 'Visit ESDC Website',

    // Terms of Service (Task 29.1B)
    'terms.title': 'Terms of Service',
    'terms.lastUpdated': 'Last Updated',
    'terms.intro.title': 'Introduction',
    'terms.intro.description': 'These Terms of Service govern your use of OaSIS Explorer. By accessing or using our service, you agree to be bound by these terms.',
    'terms.acceptance.title': 'Acceptance of Terms',
    'terms.acceptance.description': 'By creating an account or using OaSIS Explorer, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy.',
    'terms.service.title': 'Description of Service',
    'terms.service.description': 'OaSIS Explorer provides access to OaSIS occupational datasets maintained by Employment and Social Development Canada (ESDC). Our service allows you to:',
    'terms.service.search': 'Search and filter occupations by various criteria',
    'terms.service.browse': 'Browse occupational hierarchies and taxonomies',
    'terms.service.compare': 'Compare occupations and their characteristics',
    'terms.service.save': 'Save searches and bookmark occupations (with account)',
    'terms.dataDisclaimer.title': 'Government Data Disclaimer',
    'terms.dataDisclaimer.description': 'OaSIS Explorer displays government data from OaSIS datasets AS-IS, without modification. All occupational information, measurements, and classifications are provided by ESDC.',
    'terms.dataDisclaimer.accuracy': 'We are not responsible for the accuracy, completeness, or timeliness of the government data. For official occupational information, please consult ESDC directly.',
    'terms.accounts.title': 'User Accounts',
    'terms.accounts.description': 'To access certain features, you must create an account. You are responsible for:',
    'terms.accounts.accurate': 'Providing accurate and current information',
    'terms.accounts.security': 'Maintaining the security of your password',
    'terms.accounts.activity': 'All activity that occurs under your account',
    'terms.accounts.notify': 'Notifying us immediately of any unauthorized access',
    'terms.acceptable.title': 'Acceptable Use',
    'terms.acceptable.description': 'You agree NOT to:',
    'terms.acceptable.lawful': 'Use the service for any unlawful purpose',
    'terms.acceptable.noHarm': 'Attempt to harm, disrupt, or compromise the service',
    'terms.acceptable.noAutomation': 'Use automated tools to access or scrape data (without permission)',
    'terms.acceptable.noReverse': 'Reverse engineer or attempt to extract source code',
    'terms.acceptable.noResell': 'Resell or redistribute our services',
    'terms.subscriptions.title': 'Subscriptions and Payments',
    'terms.subscriptions.description': 'OaSIS Explorer offers free and paid subscription tiers:',
    'terms.subscriptions.billing.title': 'Billing',
    'terms.subscriptions.billing.description': 'Pro subscriptions are billed monthly or annually via Stripe',
    'terms.subscriptions.cancellation.title': 'Cancellation',
    'terms.subscriptions.cancellation.description': 'You may cancel anytime; access continues until period end',
    'terms.subscriptions.refunds.title': 'Refunds',
    'terms.subscriptions.refunds.description': 'No refunds for partial months or unused portions',
    'terms.subscriptions.changes.title': 'Price Changes',
    'terms.subscriptions.changes.description': 'We may change pricing with 30 days notice',
    'terms.ip.title': 'Intellectual Property',
    'terms.ip.description': 'Ownership is divided as follows:',
    'terms.ip.government.title': 'Government Data',
    'terms.ip.government.description': 'OaSIS database and occupational data are property of the Government of Canada',
    'terms.ip.application.title': 'Application',
    'terms.ip.application.description': 'OaSIS Explorer application, design, and code are proprietary',
    'terms.disclaimers.title': 'Disclaimers',
    'terms.disclaimers.asIs': 'OaSIS Explorer is provided "AS IS" without warranties of any kind:',
    'terms.disclaimers.noWarranty': 'No warranty of accuracy, reliability, or availability',
    'terms.disclaimers.noGuarantee': 'No guarantee of uninterrupted or error-free service',
    'terms.disclaimers.noLiability': 'Not responsible for decisions made based on displayed data',
    'terms.liability.title': 'Limitation of Liability',
    'terms.liability.description': 'To the maximum extent permitted by law, OaSIS Explorer shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the service.',
    'terms.availability.title': 'Service Availability',
    'terms.availability.description': 'We strive for high availability but do not guarantee uninterrupted access. We may suspend or terminate the service for maintenance, updates, or other reasons with or without notice.',
    'terms.changes.title': 'Changes to Terms',
    'terms.changes.description': 'We reserve the right to modify these terms at any time. Material changes will be communicated via email or website notice. Continued use after changes constitutes acceptance.',
    'terms.termination.title': 'Termination',
    'terms.termination.description': 'We may suspend or terminate your account for violation of these terms, illegal activity, or at our discretion. You may terminate your account at any time through your profile settings.',
    'terms.law.title': 'Governing Law',
    'terms.law.description': 'These terms are governed by the laws of Canada. Any disputes shall be resolved in Canadian courts.',
    'terms.contact.title': 'Contact Us',
    'terms.contact.description': 'If you have questions about these Terms of Service, please contact us:',
  },
  fr: {
    // Navigation
    'nav.home': 'Accueil',
    'nav.occupations': 'Recherche',
    'nav.taxonomy': 'Professions',
    'nav.measurements': 'Compétences & Aptitudes',
    'nav.classification': 'Professions',
    'nav.sct': 'Compétences',
    'nav.about': 'À propos',
    'nav.datasets': 'Jeux de données',
    'nav.user': 'Utilisateur',
    'nav.savedSearches': 'Recherches sauvegardees',
    'version.provenance.snapshotCaptured': 'Capture de la source',
    'version.provenance.packageModified': 'Paquet gouvernemental modifie',
    'version.provenance.notAvailable': 'Non disponible',
    'discrepancy.search.title': 'Divergence de donnees connue',
    'discrepancy.search.message': 'Un ou plusieurs filtres selectionnes ont des divergences connues entre la recherche avancee OaSIS officielle et les donnees ouvertes. Les resultats affiches ici utilisent les valeurs Open Data.',
    'discrepancy.search.affectedFilters': 'Filtres selectionnes touches',
    'discrepancy.occupation.title': 'Divergence de donnees connue',
    'discrepancy.occupation.message': 'Cette profession apparait dans des lignes de divergence connues.',
    'discrepancy.occupation.messagePrefix': 'Cette profession apparait dans des lignes de divergence connues entre',
    'discrepancy.occupation.messageAnd': 'et',
    'discrepancy.occupation.affectedFields': '{count} champs touches',
    'discrepancy.occupation.showDetails': 'Afficher les details des divergences ({count})',
    'discrepancy.occupation.showLess': 'Afficher moins',
    'discrepancy.occupation.showingRows': 'Affichage de {shown} sur {total} lignes de divergence',
    'discrepancy.common.officialValue': 'Valeur officielle',
    'discrepancy.common.openDataValue': 'Valeur Open Data',
    
    // Language switching
    'language.switch': 'Passer à l\'anglais',
    'language.current': 'Français',
    
    // Theme switching
    'theme.switch': 'Passer au mode sombre',
    'theme.light': 'Mode clair',
    'theme.dark': 'Mode sombre',
    
    // Common UI
    'common.loading': 'Chargement...',
    'common.error': 'Une erreur est survenue',
    'common.retry': 'Réessayer',
    'common.back': 'Retour',
    'common.next': 'Suivant',
    'common.previous': 'Précédent',
    'common.search': 'Rechercher',
    'common.clear': 'Effacer',
    'common.close': 'Fermer',
    'common.cancel': 'Annuler',
    'common.email': 'Courriel',
    
    // App title and description
    'app.title': 'OaSIS Explorer',
    'app.subtitle': 'Interface Alternative pour le Système d\'Information sur les Professions et les Compétences du Gouvernement du Canada',
    'app.description': 'Explorez les professions en utilisant les données officielles d\'EDSC du gouvernement du Canada',
    'app.welcome': 'Bienvenue dans l\'OaSIS Explorer',
    'app.official.title': 'Données Gouvernementales Officielles',
    'app.official.description': 'Ceci est une interface alternative pour le Système d\'Information sur les Professions et les Compétences (SIPEC) d\'Emploi et Développement social Canada. Toutes les données proviennent directement des jeux de données gouvernementaux officiels sans modification.',
    'app.data.source': 'Source des Données',
    'app.data.dataset': 'Jeu de Données',
    'app.data.program': 'Disponible via le Programme de Gouvernement Ouvert du Canada',
    'app.links.official': 'Système OaSIS Officiel',
    'app.links.dataset': 'Portail de Données Ouvertes Canada',
    'app.links.government': 'Gouvernement Ouvert Canada',
    
    // Feature badges
    'badges.core': 'Fonctionnalité Principale',
    'badges.navigation': 'Navigation',
    
    // Feature cards
    'features.search.title': 'Recherche Avancée',
    'features.search.description': 'Trouvez et explorez des informations détaillées sur les professions canadiennes avec des capacités de recherche avancées.',
    'features.search.button': 'Commencer la Recherche',
    'features.taxonomy.title': 'Naviguer dans la Taxonomie des Professions',
    'features.taxonomy.description': 'Naviguez dans le système de classification structuré des classifications et catégories professionnelles.',
    'features.taxonomy.button': 'Explorer la taxonomie',
    'features.measurements.title': 'Naviguer dans la Taxonomie des Compétences & Aptitudes',
    'features.measurements.description': 'Explorez la hiérarchie de la Taxonomie des Compétences et des Compétences (TCC) avec descripteurs de mesure et outils d\'analyse.',
    'features.measurements.button': 'Explorer la taxonomie',
    'features.details.title': 'Détails des Professions',
    'features.details.description': 'Accédez aux profils complets des professions avec compétences et exigences.',
    'features.details.button': 'Parcourir les Profils Professionnels',
    'features.about.title': 'À propos et Documentation',
    'features.about.description': 'Découvrez le projet Système d\'Information sur les Professions et les Compétences, les sources de données et la documentation technique.',
    'features.about.button': 'En savoir plus',
    
    // Homepage content
    'home.hero.explore': 'Explorez les Données d\'Emploi Canadiennes',
    'home.hero.subtitle': 'Accédez aux outils d\'analyse d\'emploi complets avec recherche avancée, navigation du système de classification et mesures détaillées.',
    'home.benefits.title': 'Pourquoi Choisir le Système d\'Information sur les Professions et les Compétences?',
    'home.benefits.subtitle': 'Conçu pour les chercheurs, conseillers en carrière et décideurs politiques avec une technologie de pointe et un design centré sur l\'utilisateur.',
    'home.benefits.fast.title': 'Ultra Rapide',
    'home.benefits.fast.description': 'Interactions sub-200ms, builds optimisés et design réactif pour une excellente expérience utilisateur.',
    'home.benefits.saved.title': 'Recherches Sauvegardées',
    'home.benefits.saved.description': 'Sauvegardez vos recherches pour plus tard et revenez rapidement à votre recherche sans recommencer.',
    'home.benefits.navigate.title': 'Navigation Intuitive',
    'home.benefits.navigate.description': 'Interface rationalisée conçue pour les chercheurs avec des chemins de navigation clairs et une organisation logique de l\'information.',
    'home.stats.occupations': 'Professions',
    'home.stats.measurements': 'Mesures',
    
    // Advanced Search page
    'search.title': 'Recherche Avancée',
    'search.subtitle': 'Trouvez des professions en utilisant des capacités de recherche et de filtrage complètes',
    'search.autocomplete.placeholder': 'Commencez à taper pour rechercher des professions...',
    'search.results.count': 'professions correspondent à vos critères',
    'search.results.showing': 'Affichage',
    'search.results.of': 'de',
    'search.filters.title': 'Filtres',
    'search.filters.clear': 'Effacer Tous les Filtres',
    'search.filters.apply': 'Appliquer les Filtres',
    'search.filters.textFilter': 'Filtre de Texte',
    'search.filters.textFilter.placeholder': 'Entrez le titre de la profession ou des mots-clés...',
    'search.filters.hierarchyCode': 'Filtre de Catégorie Large',
    'search.filters.hierarchyCode.description': 'Sélectionnez les catégories de professions pour filtrer par code de classification',
    'search.filters.teerLevel': 'Filtre de Niveau TEER',
    'search.filters.teerLevel.description': 'Sélectionnez les niveaux TEER pour filtrer par formation, éducation, expérience et responsabilités',
    'search.filters.teer.0': 'TEER 0 - Professions de gestion',
    'search.filters.teer.1': 'TEER 1 - Professions nécessitant généralement un diplôme universitaire',
    'search.filters.teer.2': 'TEER 2 - Professions nécessitant généralement un diplôme collégial ou une formation d\'apprentissage de deux ans ou plus; ou professions de supervision',
    'search.filters.teer.3': 'TEER 3 - Professions nécessitant généralement un diplôme collégial ou une formation d\'apprentissage de moins de deux ans; ou plus de six mois de formation en cours d\'emploi',
    'search.filters.teer.4': 'TEER 4 - Professions nécessitant généralement un diplôme d\'études secondaires; ou plusieurs semaines de formation en cours d\'emploi',
    'search.filters.teer.5': 'TEER 5 - Professions nécessitant une démonstration de travail à court terme et aucune éducation formelle',
    'search.filters.measurements': 'Filtres de Mesure',
    'search.filters.measurements.description': 'Filtrer par compétences, aptitudes, contexte de travail et autres mesures',
    'search.filters.hierarchy': 'Filtre de Structure de Classification',
    'search.filters.text': 'Recherche Textuelle',
    'search.filters.category': 'Catégorie',
    'search.filters.teer': 'TEER',
    'search.filters.range.min': 'Minimum',
    'search.filters.range.max': 'Maximum',
    'search.filters.range.selected': 'Plage sélectionnée',
    'search.filters.range.none': 'Aucune plage sélectionnée',
    'search.filters.track.mobile.instructions': 'Appuyez pour définir la valeur minimale',
    'search.filters.track.mobile.tapMax': 'Appuyez pour définir la valeur maximale',
    'search.filters.track.mobile.tapMin': 'Appuyez pour définir une nouvelle valeur minimale',
    'search.loading': 'Recherche en cours...',
    'search.error': 'Échec de la recherche. Veuillez réessayer.',
    'search.no-results': 'Aucune profession trouvée correspondant à vos critères.',
    'search.suggestions': 'Suggestions de recherche',
    'search.min-chars': 'Tapez au moins 2 caractères pour rechercher',
    'search.saveButton': 'Enregistrer cette recherche',
    // Task 30.7: Manual search button
    'search.searchButton': 'Rechercher des professions',
    'search.searching': 'Recherche en cours...',
    'search.readyToSearch': 'Pret a rechercher',
    'search.clickToExecute': 'Cliquez sur le bouton pour trouver des professions correspondantes.',
    'search.loadingCount': 'Comptage des professions correspondantes...',
    'search.countPreview': 'professions correspondent à vos filtres',
    'search.saveDialog.title': 'Enregistrer la Recherche',
    'search.saveDialog.nameLabel': 'Nom de la Recherche',
    'search.saveDialog.namePlaceholder': 'Entrez un nom pour cette recherche...',
    'search.saveDialog.characters': 'caractères',
    'search.saveDialog.errorEmpty': 'Veuillez entrer un nom pour cette recherche',
    'search.saveDialog.errorTooLong': 'Le nom de la recherche doit contenir 50 caractères ou moins',
    'search.saveDialog.errorDuplicate': 'Une recherche avec ce nom existe déjà',
    'search.saveDialog.saveButton': 'Enregistrer',
    'search.saveSuccess': 'Recherche enregistrée sous "{name}"',
    'search.refineYourSearch': 'Affinez votre recherche',
    'search.adjustFiltersToRefine': 'Ajustez les filtres ci-dessous pour affiner vos résultats',
    
    // Search Results page
    'searchResults.title': 'Résultats de Recherche',
    'searchResults.backToFilters': 'Retour aux Filtres',
    'searchResults.viewFullResults': 'Voir les Résultats',
    'searchResults.noResultsFound': 'Aucun résultat trouvé',
    'searchResults.tryAdjustingFilters': 'Essayez d\'ajuster vos filtres ou vos termes de recherche',
    'searchResults.loadingResults': 'Chargement des résultats...',
    'searchResults.error': 'Erreur lors du Chargement des Résultats',
    'searchResults.sortBy': 'Trier par',
    'searchResults.sortTitleAsc': 'Titre (A-Z)',
    'searchResults.sortTitleDesc': 'Titre (Z-A)',
    'searchResults.sortCodeAsc': 'Code (Croissant)',
    'searchResults.sortCodeDesc': 'Code (Décroissant)',
    
    // User page
    'user.title': 'Mes Recherches Enregistrées',
    'user.subtitle': 'Accédez et gérez vos configurations de recherche enregistrées',
    'user.savedSearches': 'recherches enregistrées',
    'user.loading': 'Chargement des recherches enregistrées...',
    'user.empty.title': 'Aucune recherche enregistrée pour le moment',
    'user.empty.description': 'Enregistrez vos configurations de recherche sur la page de Recherche Avancée pour y accéder ici plus tard.',
    'user.empty.goToSearch': 'Aller à la Recherche Avancée',
    'user.bookmarked.title': 'Favoris',
    'user.allSearches.title': 'Toutes les Recherches Enregistrées',
    'user.filterByTags': 'Filtrer par Étiquettes',
    'user.filterButton': 'Filtrer',
    'user.noFilteredResults': 'Aucune recherche enregistrée ne correspond aux étiquettes sélectionnées.',
    'user.deleteConfirm.title': 'Supprimer la Recherche Enregistrée',
    'user.deleteConfirm.message': 'Êtes-vous sûr de vouloir supprimer "{name}"? Cette action ne peut pas être annulée.',
    'user.deleteConfirm.deleteButton': 'Supprimer',
    
    // Taxonomy page
    'taxonomy.title': 'Professions',
    'taxonomy.subtitle': 'Naviguer dans la structure du système de classification des professions',
    
    // Classification page
    'classification.treeView': 'Arborescence',
    'classification.listView': 'Tableau',
    'classification.treeTitle': 'Arborescence des Professions',
    'classification.listTitle': 'Tableau des Professions',
    'classification.occupations': 'professions',
    'classification.oasisCode': 'Code OaSIS',
    'classification.occupationTitle': 'Titre de la profession',
    'classification.noResults': 'Aucune profession ne correspond à votre recherche',
    'classification.noOccupations': 'Aucune profession trouvée',
    'classification.loading': 'Chargement des professions...',
    'classification.error': 'Échec du chargement des professions. Veuillez réessayer.',
    'classification.expandHint': 'Développez les éléments pour voir la hiérarchie',
    'classification.broadOccCat': 'Cat. prof. générale',
    'classification.teer': 'TEER',
    'classification.majorGroup': 'Grand groupe',
    'classification.subMajorGroup': 'Sous-grand groupe',
    'classification.minorGroup': 'Groupe mineur',
    'classification.nocUnitGroup': 'Groupe unitaire CNP',
    
    // Measurements page
    'measurements.title': 'Compétences & Aptitudes',
    'measurements.subtitle': 'Naviguer dans la structure des compétences et des aptitudes (TCC)',
    'measurements.search.placeholder': 'Rechercher le code TCC ou le titre du descripteur...',
    'classification.search.placeholder': 'Rechercher le code OaSIS ou le titre de la profession...',
    'measurements.coming.title': 'Navigation de la Taxonomie des Compétences et des Compétences (TCC) Bientôt Disponible',
    'measurements.coming.description': 'La navigation interactive de la Taxonomie des Compétences et des Compétences (TCC) avec recherche, filtrage et exploration des mesures sera disponible dans la prochaine phase.',
    
    // SCT Table View
    'sct.table.treeView': 'Arborescence',
    'sct.table.listView': 'Tableau',
    'sct.table.entries': 'entrées',
    'sct.table.code': 'Code TCC',
    'sct.table.title': 'Titre',
    'sct.table.hierarchyLevel': 'Niveau hiérarchique',
    'sct.table.category': 'Catégorie',
    'sct.table.subCategory': 'Sous-catégorie',
    'sct.table.similarityGroup': 'Groupe de similarité',
    'sct.table.descriptor': 'Descripteur',
    'sct.table.noResults': 'Aucune entrée ne correspond à votre recherche',
    'sct.table.noEntries': 'Aucune entrée trouvée',
    'sct.table.loading': 'Chargement des entrées...',
    'sct.table.error': 'Échec du chargement des entrées. Veuillez réessayer.',
    'sct.table.expandHint': 'Développez les éléments pour voir la hiérarchie',
    'sct.treeTitle': 'Arborescence TCC',
    'sct.listTitle': 'Tableau TCC',
    'measurements.structure.title': 'Structure de la Taxonomie des Compétences et des Compétences (TCC)',

    // Occupation details page
    'occupation.details.title': 'Détails de l\'Occupation',
    'occupation.details.loading': 'Chargement des détails de l\'occupation...',
    'occupation.details.notFound': 'Occupation Non Trouvée',
    'occupation.details.error': 'Erreur de Chargement de l\'Occupation',
    'occupation.details.back': 'Retour',
    'occupation.details.export': 'Exporter le Profil',
    'occupation.details.overview': 'Aperçu de l\'Occupation',
    'occupation.details.statistics': 'Statistiques des Données',
    'occupation.details.completeness': 'Complétude des Données',
    'occupation.details.totalMeasurements': 'Mesures Totales',
    'occupation.details.withValues': 'Avec Valeurs',
    'occupation.details.measurements': 'Mesures de la Taxonomie des Compétences et des Compétences (TCC)',
    'occupation.details.interests': 'Profil d\'Intérêts (Codes Holland)',
    'occupation.details.additional': 'Informations Supplémentaires',
    'occupation.details.requirements': 'Exigences d\'Emploi',
    'occupation.details.duties': 'Tâches Principales',
    'occupation.details.titles': 'Exemples de Titres d\'Emploi',
    'occupation.details.titles.more': 'de plus',
    'occupation.details.titles.showMore': 'Afficher tous les titres d\'emploi',
    'occupation.details.titles.showLess': 'Afficher moins',
    'occupation.details.workplace': 'Informations sur le Lieu de Travail',
    'occupation.findSimilar': 'Trouver des Professions Similaires',
    'occupation.similarOccupations': 'Professions Similaires',
    'occupation.similarOccupationsDescription': 'Rechercher des professions avec des compétences, capacités, connaissances et contexte de travail similaires.',
    'occupation.prefillModal.title': 'Trouver des Professions Similaires',
    'occupation.prefillModal.description': 'Sélectionnez les caractéristiques à utiliser pour trouver des professions similaires',
    'occupation.prefillModal.searchMode': 'Inclure les professions avec des valeurs inférieures',
    'occupation.prefillModal.searchModeExact': 'Correspondance exacte : Trouver des professions avec les mêmes valeurs',
    'occupation.prefillModal.searchModeLTE': 'Correspondance de plage : Trouver des professions avec des valeurs de 0 aux valeurs de cette profession',
    'occupation.prefillModal.categories': 'Catégories',
    'occupation.prefillModal.selectAll': 'Tout',
    'occupation.prefillModal.deselectAll': 'Aucun',
    'occupation.prefillModal.categoriesSelected': '{{count}} catégories sélectionnées',
    'occupation.prefillModal.noCategoriesSelected': 'Veuillez sélectionner au moins une catégorie',
    'occupation.prefillModal.noCategories': 'Aucune catégorie de mesure disponible',
    'occupation.prefillModal.countLoading': 'Comptage des résultats...',
    'occupation.prefillModal.countError': 'Impossible de compter les résultats',
    'occupation.prefillModal.resultCount': '{{count}} professions correspondantes ({{time}}ms)',
    'occupation.prefillModal.search': 'Rechercher',
    'occupation.prefillModal.cancel': 'Annuler',
    
    // Core Competencies (Task 33.4 - 2025 only)
    'occupation.coreCompetencies.title': 'Competences cles',
    'occupation.coreCompetencies.description': 'Competences cles requises pour cette profession',
    'occupation.coreCompetencies.loading': 'Chargement des competences...',
    
    // Skills Match (Task 33.6B - 2025 only)
    'occupation.skillsMatch.description': 'Professions avec des profils similaires en competences, capacites et connaissances selon l\'analyse OaSIS.',
    'occupation.skillsMatch.unavailable': 'Les recommandations de correspondance directe ne sont pas disponibles pour cette version. Utilisez le bouton ci-dessous pour rechercher manuellement.',
    'occupation.skillsMatch.empty': 'Aucune donnee de correspondance disponible pour cette profession.',
    'occupation.skillsMatch.error': 'Impossible de charger les recommandations. Essayez de rafraichir la page.',
    'occupation.skillsMatch.verySimilar': 'Tres similaire',
    'occupation.skillsMatch.similar': 'Similaire',
    'occupation.skillsMatch.somewhatSimilar': 'Assez similaire',
    'occupation.skillsMatch.notSimilar': 'Peu similaire',
    
    // Footer links
    'footer.github': 'GitHub',
    'footer.documentation': 'Documentation',
    'footer.api': 'Référence API',
    
    // Error messages
    'error.network': 'Erreur de connexion réseau',
    'error.api': 'Échec de la requête API',
    'error.notFound': 'Ressource non trouvée',
    'error.serverError': 'Erreur serveur survenue',
    'error.tryAgain': 'Veuillez réessayer plus tard',
    
    // Accessibility
    'a11y.skipToContent': 'Aller au contenu principal',
    'a11y.mainMenu': 'Menu de navigation principal',
    'a11y.languageToggle': 'Changer de langue',
    'a11y.themeToggle': 'Basculer le mode sombre',
    
    // Authentication
    'auth.signIn': 'Se connecter',
    'auth.signUp': "S'inscrire",
    'auth.signOut': 'Se deconnecter',
    'auth.signingIn': 'Connexion en cours...',
    'auth.creatingAccount': 'Creation du compte...',
    'auth.createAccount': 'Creer un compte',
    'auth.email': 'Courriel',
    'auth.password': 'Mot de passe',
    'auth.confirmPassword': 'Confirmer le mot de passe',
    'auth.displayName': 'Nom affiche',
    'auth.forgotPassword': 'Mot de passe oublie?',
    'auth.noAccount': "Vous n'avez pas de compte?",
    'auth.haveAccount': 'Vous avez deja un compte?',
    'auth.resetPassword': 'Reinitialiser le mot de passe',
    'auth.resetPasswordDescription': 'Entrez votre courriel et nous vous enverrons un lien pour reinitialiser votre mot de passe.',
    'auth.sendResetLink': 'Envoyer le lien',
    'auth.sending': 'Envoi en cours...',
    'auth.resetEmailSent': 'Verifiez votre courriel',
    'auth.resetEmailSentDescription': 'Nous avons envoye un lien de reinitialisation a votre adresse courriel.',
    'auth.backToSignIn': 'Retour a la connexion',
    'auth.passwordMismatch': 'Les mots de passe ne correspondent pas.',
    'auth.passwordTooShort': 'Le mot de passe doit contenir au moins 6 caracteres.',

    // Auth Prompt Modal (Task 30.5)
    'auth.prompt.title': 'Connectez-vous pour acceder a {feature}',
    'auth.prompt.description': 'Creez un compte gratuit pour debloquer cette fonctionnalite.',
    'auth.prompt.benefitsTitle': 'Avec un compte gratuit, vous pouvez:',
    'auth.prompt.benefit.savedSearches': 'Sauvegarder jusqu\'a 3 recherches',
    'auth.prompt.benefit.moreSearches': '{freeLimit} recherches par jour (vs {anonymousLimit} anonyme)',
    'auth.prompt.benefit.crossDevice': 'Acceder depuis n\'importe quel appareil',
    'auth.prompt.signIn': 'Se connecter',
    'auth.prompt.createAccount': 'Creer un compte gratuit',
    'auth.prompt.maybeLater': 'Peut-etre plus tard',
    
    // Common additions
    'common.optional': 'optionnel',
    'common.edit': 'Modifier',
    'common.save': 'Enregistrer',
    
    // Profile page
    'profile.title': 'Profil et parametres',
    'profile.subtitle': 'Gerez votre compte et vos preferences',
    'profile.profileSection': 'Profil',
    'profile.settingsSection': 'Parametres',
    'profile.accountSection': 'Compte',
    'profile.displayName': "Nom d'affichage",
    'profile.noName': 'Aucun nom defini',
    'profile.email': 'Courriel',
    'profile.emailReadOnly': 'Le courriel ne peut pas etre modifie',
    'profile.memberSince': 'Membre depuis',
    'profile.language': 'Langue',
    'profile.theme': 'Theme',
    'profile.themeLight': 'Clair',
    'profile.themeDark': 'Sombre',
    'profile.changePassword': 'Changer le mot de passe',
    'profile.passwordResetSent': 'Courriel envoye! Verifiez votre boite de reception.',
    'profile.passwordResetError': 'Echec de l\'envoi du courriel.',
    'nav.profile': 'Profil',
    
    // Subscription
    'profile.subscriptionSection': 'Abonnement',
    'profile.currentPlan': 'Plan actuel',
    'profile.freePlan': 'Gratuit',
    'profile.proPlan': 'Pro',
    'profile.upgradeToProTitle': 'Passer a Pro',
    'profile.upgradeToProDescription': 'Debloquez toutes les fonctionnalites, y compris les recherches et exportations illimitees.',
    'profile.monthly': 'Mensuel',
    'profile.annual': 'Annuel',
    'profile.monthlyPrice': '50$/mois',
    'profile.annualPrice': '480$/an',
    'profile.annualSavings': 'Economisez 20%',
    'profile.upgradeButton': 'Mettre a niveau',
    'profile.manageSubscription': 'Gerer l\'abonnement',
    'profile.nextBilling': 'Prochaine facturation',
    'profile.cancelingOn': 'Annulation le',
    'profile.pastDue': 'Paiement en retard - veuillez mettre a jour votre methode de paiement.',
    'profile.updatePayment': 'Mettre a jour le paiement',
    'profile.checkoutSuccess': 'Bienvenue chez Pro! Votre abonnement est maintenant actif.',
    'profile.checkoutCanceled': 'Paiement annule. Aucun frais n\'a ete effectue.',
    'profile.subscriptionError': 'Echec de la recuperation du statut d\'abonnement. Veuillez reessayer plus tard.',
    'profile.profileError': 'Echec de la recuperation des informations de profil. Veuillez reessayer plus tard.',

    // Search Usage Banner (Task 30.9)
    'usage.banner.freePlan': 'Plan gratuit',
    'usage.banner.guest': 'Invite',
    'usage.banner.searchesLeft': '{count} recherches restantes aujourd\'hui',
    'usage.banner.oneSearchLeft': '1 recherche restante aujourd\'hui',
    'usage.banner.noSearchesLeft': 'Aucune recherche restante aujourd\'hui',
    'usage.banner.runningLow': 'Presque epuise!',
    'usage.banner.resetsIn': 'Reinitialisation dans {hours}h',
    'usage.banner.getUnlimited': 'Obtenir illimite',
    'usage.banner.signUpFree': 'Inscription gratuite',

    // Usage Limits (Task 30.2)
    'usage.remaining': '{remaining} de {limit} recherches restantes aujourd\'hui',
    'usage.remainingOne': '1 recherche restante aujourd\'hui',
    'usage.remainingNone': 'Aucune recherche restante aujourd\'hui',
    'usage.unlimited': 'Recherches illimitees',
    'usage.resetsIn': 'Reinitialisation dans {hours} heures',
    'usage.resetsAt': 'Reinitialisation a {time}',

    // Limit Reached Modal (Task 30.2)
    'limit.title': 'Limite de recherche quotidienne atteinte',
    'limit.messageAnonymous': 'Les utilisateurs anonymes peuvent faire {anonymousLimit} recherches par jour.',
    'limit.messageFree': 'Les comptes gratuits peuvent faire {freeLimit} recherches par jour.',
    'limit.resetTime': 'Votre limite se reinitialise dans {hours} heures.',
    'limit.signUpFree': 'Inscription gratuite',
    'limit.signUpBenefit': 'Obtenez {freeLimit} recherches par jour avec un compte gratuit!',
    'limit.upgradeToPro': 'Passer a Pro',
    'limit.upgradeBenefit': 'Recherches illimitees et fonctionnalites premium.',
    'limit.maybeLater': 'Peut-etre plus tard',
    'limit.waitForReset': 'Attendre la reinitialisation',

    // Upgrade CTA (Task 30.2)
    'upgrade.title': 'Passer a Pro',
    'upgrade.description': 'Obtenez des recherches illimitees et des fonctionnalites premium.',
    'upgrade.cta': 'Mettre a niveau maintenant',
    'upgrade.benefits.unlimited': 'Recherches illimitees',
    'upgrade.benefits.filters': 'Tous les types de filtres, y compris les interets',
    'upgrade.benefits.exports': 'Exporter les donnees en CSV/PDF',
    'upgrade.benefits.savedSearches': 'Recherches sauvegardees illimitees',

    // Feature Gating (Task 30.6)
    'feature.proBadge': 'Pro',
    'feature.locked': 'Verrouille',
    'feature.proOnly': 'Pro uniquement',
    'feature.accountRequired': 'Compte requis',
    'feature.upgradeToUse': 'Passez a Pro pour utiliser {feature}',
    'feature.signUpToUse': 'Inscrivez-vous gratuitement pour utiliser {feature}',
    'feature.tooltip': 'Cette fonctionnalite est uniquement disponible pour les utilisateurs Pro',
    'feature.accessDenied.title': 'Fonctionnalite Pro',
    'feature.accessDenied.message': '{feature} est uniquement disponible pour les utilisateurs Pro.',
    'feature.accessDenied.upgrade': 'Passer a Pro',
    'feature.accessDenied.maybeLater': 'Peut-etre plus tard',
    'feature.interestsFilter': 'Filtre d\'interets',
    'feature.interestsFilterDescription': 'Filtrer les professions par interets de code Holland (RIASEC)',

    // Privacy Policy (Task 29.1A)
    'privacy.title': 'Politique de confidentialite',
    'privacy.lastUpdated': 'Derniere mise a jour',
    'privacy.intro.title': 'Introduction',
    'privacy.intro.description': 'OaSIS Explorer s\'engage a proteger votre vie privee. Cette politique de confidentialite explique comment nous collectons, utilisons et protegeons vos informations lorsque vous utilisez notre service.',
    'privacy.collection.title': 'Informations que nous collectons',
    'privacy.collection.description': 'Nous collectons les types d\'informations suivants:',
    'privacy.collection.email.title': 'Adresse courriel',
    'privacy.collection.email.description': 'Utilisee pour l\'authentification et la gestion du compte',
    'privacy.collection.profile.title': 'Informations de profil',
    'privacy.collection.profile.description': 'Nom d\'affichage et preferences optionnels que vous fournissez',
    'privacy.collection.usage.title': 'Donnees d\'utilisation',
    'privacy.collection.usage.description': 'Requetes de recherche, recherches sauvegardees, professions favorites et utilisation des fonctionnalites',
    'privacy.collection.technical.title': 'Donnees techniques',
    'privacy.collection.technical.description': 'Adresse IP, type de navigateur, informations sur l\'appareil et donnees de session',
    'privacy.usage.title': 'Comment nous utilisons vos informations',
    'privacy.usage.auth': 'Pour fournir l\'authentification et l\'acces au compte',
    'privacy.usage.service': 'Pour fournir et ameliorer nos services',
    'privacy.usage.communication': 'Pour envoyer des courriels transactionnels (reinitialisations de mot de passe, confirmations d\'abonnement)',
    'privacy.usage.analytics': 'Pour analyser les modeles d\'utilisation et ameliorer l\'experience utilisateur',
    'privacy.usage.improvement': 'Pour developper de nouvelles fonctionnalites et maintenir la qualite du service',
    'privacy.storage.title': 'Stockage et traitement des donnees',
    'privacy.storage.description': 'Vos donnees sont stockees en toute securite selon les normes de l\'industrie:',
    'privacy.storage.firebase': 'Gere l\'authentification et la verification des courriels',
    'privacy.storage.gcp': 'Heberge notre application et notre infrastructure de base de donnees',
    'privacy.storage.database': 'Stocke les profils utilisateurs, les abonnements et les donnees sauvegardees',
    'privacy.thirdParty.title': 'Services tiers',
    'privacy.thirdParty.firebase': 'Services d\'authentification',
    'privacy.thirdParty.resend': 'Livraison de courriels transactionnels (reinitialisations de mot de passe, notifications)',
    'privacy.thirdParty.stripe': 'Traitement des paiements pour les abonnements Pro (nous ne stockons pas les details de paiement)',
    'privacy.thirdParty.policies': 'Ces services ont leurs propres politiques de confidentialite et pratiques de securite.',
    'privacy.cookies.title': 'Cookies et suivi',
    'privacy.cookies.description': 'Nous utilisons des cookies et des technologies similaires pour:',
    'privacy.cookies.auth.title': 'Authentification',
    'privacy.cookies.auth.description': 'Pour vous garder connecte en toute securite',
    'privacy.cookies.session.title': 'Gestion de session',
    'privacy.cookies.session.description': 'Pour suivre l\'utilisation anonyme pour la limitation de debit',
    'privacy.cookies.preferences.title': 'Preferences',
    'privacy.cookies.preferences.description': 'Pour memoriser vos choix de langue et de theme',
    'privacy.rights.title': 'Vos droits',
    'privacy.rights.access.title': 'Acces',
    'privacy.rights.access.description': 'Consultez vos donnees personnelles via votre page de profil',
    'privacy.rights.correct.title': 'Correction',
    'privacy.rights.correct.description': 'Mettez a jour vos informations de profil a tout moment',
    'privacy.rights.delete.title': 'Suppression',
    'privacy.rights.delete.description': 'Demandez la suppression de votre compte en nous contactant',
    'privacy.rights.export.title': 'Exportation',
    'privacy.rights.export.description': 'Demandez une copie de vos donnees',
    'privacy.security.title': 'Securite des donnees',
    'privacy.security.description': 'Nous mettons en oeuvre des mesures de securite techniques et organisationnelles appropriees pour proteger vos informations personnelles, y compris le chiffrement, l\'authentification securisee et des audits de securite reguliers.',
    'privacy.children.title': 'Confidentialite des enfants',
    'privacy.children.description': 'OaSIS Explorer n\'est pas destine aux enfants de moins de 13 ans. Nous ne collectons pas sciemment d\'informations personnelles aupres d\'enfants.',
    'privacy.changes.title': 'Modifications de cette politique',
    'privacy.changes.description': 'Nous pouvons mettre a jour cette politique de confidentialite de temps a autre. Nous informerons les utilisateurs de tout changement important par courriel ou par un avis sur notre site Web.',
    'privacy.contact.title': 'Nous contacter',
    'privacy.contact.description': 'Si vous avez des questions sur cette politique de confidentialite ou vos donnees, veuillez nous contacter:',

    // Navigation
    'nav.privacy': 'Confidentialite',
    'nav.terms': 'Conditions',
    'nav.contact': 'Contact',

    // Footer (Task 29.1C)
    'footer.about.description': 'Un outil complet pour explorer les données d\'EDSC',
    'footer.links.title': 'Liens rapides',
    'footer.contact.title': 'Nous contacter',
    'footer.contact.supportHours': 'Nous repondons generalement dans les 24-48 heures',
    'footer.rights': 'Tous droits reserves.',
    'footer.dataSource': 'Donnees fournies par Emploi et Developpement social Canada (EDSC)',

    // Contact Page (Task 29.1C)
    'contact.title': 'Nous contacter',
    'contact.description': 'Si vous avez des questions, des commentaires ou besoin d\'aide avec OaSIS Explorer, n\'hesitez pas a nous contacter.',
    'contact.methods.title': 'Comment nous joindre',
    'contact.methods.email.title': 'Support par courriel',
    'contact.methods.email.description': 'Pour les demandes generales, les problemes de compte ou le support technique:',
    'contact.methods.email.responseTime': 'Temps de reponse typique: 24-48 heures',
    'contact.hours.title': 'Heures de support',
    'contact.hours.description': 'Lundi - Vendredi, 9h00 - 17h00 HNE (sauf jours feries)',
    'contact.topics.title': 'Sujets de demandes courants',
    'contact.topics.account.title': 'Compte et authentification',
    'contact.topics.account.description': 'Reinitialisation de mot de passe, suppression de compte, problemes de connexion',
    'contact.topics.billing.title': 'Facturation et abonnements',
    'contact.topics.billing.description': 'Questions de paiement, changements de forfait, factures',
    'contact.topics.technical.title': 'Problemes techniques',
    'contact.topics.technical.description': 'Bogues, erreurs, demandes de fonctionnalites',
    'contact.topics.data.title': 'Questions sur les donnees',
    'contact.topics.data.description': 'Exactitude ou interpretation des donnees professionnelles (voir la note ci-dessous)',
    'contact.topics.feedback.title': 'Retroaction',
    'contact.topics.feedback.description': 'Suggestions, demandes de fonctionnalites, commentaires generaux',
    'contact.dataInquiries.title': 'Demandes liees aux donnees',
    'contact.dataInquiries.description': 'Pour des questions sur l\'exactitude ou l\'interpretation des donnees professionnelles, veuillez contacter Emploi et Developpement social Canada (EDSC) directement, car ils maintiennent la base de donnees OaSIS:',
    'contact.dataInquiries.link': 'Visiter le site Web d\'EDSC',

    // Terms of Service (Task 29.1B)
    'terms.title': 'Conditions d\'utilisation',
    'terms.lastUpdated': 'Derniere mise a jour',
    'terms.intro.title': 'Introduction',
    'terms.intro.description': 'Ces conditions d\'utilisation regissent votre utilisation d\'OaSIS Explorer. En accedant ou en utilisant notre service, vous acceptez d\'etre lie par ces conditions.',
    'terms.acceptance.title': 'Acceptation des conditions',
    'terms.acceptance.description': 'En creant un compte ou en utilisant OaSIS Explorer, vous reconnaissez avoir lu, compris et accepte d\'etre lie par ces conditions d\'utilisation et notre politique de confidentialite.',
    'terms.service.title': 'Description du service',
    'terms.service.description': 'OaSIS Explorer fournit un acces aux jeux de donnees professionnels OaSIS maintenus par Emploi et Developpement social Canada (EDSC). Notre service vous permet de:',
    'terms.service.search': 'Rechercher et filtrer les professions selon divers criteres',
    'terms.service.browse': 'Parcourir les hierarchies et taxonomies professionnelles',
    'terms.service.compare': 'Comparer les professions et leurs caracteristiques',
    'terms.service.save': 'Sauvegarder des recherches et marquer des professions favorites (avec compte)',
    'terms.dataDisclaimer.title': 'Avertissement concernant les donnees gouvernementales',
    'terms.dataDisclaimer.description': 'OaSIS Explorer affiche les donnees gouvernementales des jeux de donnees OaSIS TELLES QUELLES, sans modification. Toutes les informations professionnelles, mesures et classifications sont fournies par EDSC.',
    'terms.dataDisclaimer.accuracy': 'Nous ne sommes pas responsables de l\'exactitude, de l\'exhaustivite ou de l\'actualite des donnees gouvernementales. Pour des informations professionnelles officielles, veuillez consulter EDSC directement.',
    'terms.accounts.title': 'Comptes utilisateurs',
    'terms.accounts.description': 'Pour acceder a certaines fonctionnalites, vous devez creer un compte. Vous etes responsable de:',
    'terms.accounts.accurate': 'Fournir des informations exactes et a jour',
    'terms.accounts.security': 'Maintenir la securite de votre mot de passe',
    'terms.accounts.activity': 'Toute activite survenant sous votre compte',
    'terms.accounts.notify': 'Nous informer immediatement de tout acces non autorise',
    'terms.acceptable.title': 'Utilisation acceptable',
    'terms.acceptable.description': 'Vous acceptez de NE PAS:',
    'terms.acceptable.lawful': 'Utiliser le service a des fins illegales',
    'terms.acceptable.noHarm': 'Tenter de nuire, perturber ou compromettre le service',
    'terms.acceptable.noAutomation': 'Utiliser des outils automatises pour acceder ou extraire des donnees (sans permission)',
    'terms.acceptable.noReverse': 'Faire de l\'ingenierie inverse ou tenter d\'extraire le code source',
    'terms.acceptable.noResell': 'Revendre ou redistribuer nos services',
    'terms.subscriptions.title': 'Abonnements et paiements',
    'terms.subscriptions.description': 'OaSIS Explorer offre des niveaux d\'abonnement gratuits et payants:',
    'terms.subscriptions.billing.title': 'Facturation',
    'terms.subscriptions.billing.description': 'Les abonnements Pro sont factures mensuellement ou annuellement via Stripe',
    'terms.subscriptions.cancellation.title': 'Annulation',
    'terms.subscriptions.cancellation.description': 'Vous pouvez annuler a tout moment; l\'acces continue jusqu\'a la fin de la periode',
    'terms.subscriptions.refunds.title': 'Remboursements',
    'terms.subscriptions.refunds.description': 'Aucun remboursement pour les mois partiels ou portions inutilisees',
    'terms.subscriptions.changes.title': 'Changements de prix',
    'terms.subscriptions.changes.description': 'Nous pouvons modifier les prix avec un preavis de 30 jours',
    'terms.ip.title': 'Propriete intellectuelle',
    'terms.ip.description': 'La propriete est divisee comme suit:',
    'terms.ip.government.title': 'Donnees gouvernementales',
    'terms.ip.government.description': 'La base de donnees OaSIS et les donnees professionnelles sont la propriete du gouvernement du Canada',
    'terms.ip.application.title': 'Application',
    'terms.ip.application.description': 'L\'application OaSIS Explorer, la conception et le code sont proprietaires',
    'terms.disclaimers.title': 'Avertissements',
    'terms.disclaimers.asIs': 'OaSIS Explorer est fourni "TEL QUEL" sans garanties d\'aucune sorte:',
    'terms.disclaimers.noWarranty': 'Aucune garantie d\'exactitude, de fiabilite ou de disponibilite',
    'terms.disclaimers.noGuarantee': 'Aucune garantie de service ininterrompu ou sans erreur',
    'terms.disclaimers.noLiability': 'Non responsable des decisions prises sur la base des donnees affichees',
    'terms.liability.title': 'Limitation de responsabilite',
    'terms.liability.description': 'Dans la mesure maximale permise par la loi, OaSIS Explorer ne sera pas responsable des dommages indirects, accessoires, speciaux ou consecutifs decoulant de votre utilisation du service.',
    'terms.availability.title': 'Disponibilite du service',
    'terms.availability.description': 'Nous visons une haute disponibilite mais ne garantissons pas un acces ininterrompu. Nous pouvons suspendre ou interrompre le service pour maintenance, mises a jour ou autres raisons avec ou sans preavis.',
    'terms.changes.title': 'Modifications des conditions',
    'terms.changes.description': 'Nous nous reservons le droit de modifier ces conditions a tout moment. Les changements importants seront communiques par courriel ou avis sur le site Web. L\'utilisation continue apres les modifications constitue une acceptation.',
    'terms.termination.title': 'Resiliation',
    'terms.termination.description': 'Nous pouvons suspendre ou resilier votre compte en cas de violation de ces conditions, d\'activite illegale ou a notre discretion. Vous pouvez resilier votre compte a tout moment via vos parametres de profil.',
    'terms.law.title': 'Loi applicable',
    'terms.law.description': 'Ces conditions sont regies par les lois du Canada. Tout litige sera resolu devant les tribunaux canadiens.',
    'terms.contact.title': 'Nous contacter',
    'terms.contact.description': 'Si vous avez des questions sur ces conditions d\'utilisation, veuillez nous contacter:',
  },
} as const;

/**
 * Get translation for a key in the specified language
 */
export const getTranslation = (key: string, language: Language): string => {
  const languageTranslations = translations[language];
  
  // Type-safe key access with fallback
  if (key in languageTranslations) {
    return languageTranslations[key as keyof typeof languageTranslations];
  }
  
  // Fallback to English if key not found in current language
  if (language !== 'en' && key in translations.en) {
    return translations.en[key as keyof typeof translations.en];
  }
  
  // Return key as fallback if translation not found
  return key;
};

/**
 * Create translation function for a specific language
 */
export const createTranslationFunction = (language: Language) => {
  return (key: string) => getTranslation(key, language);
};

